import datepicker from '../../../../../../modules/Gull/Framework/datepicker.js';
import selectToggle from '../../../../../../modules/Gull/Framework/selectToggle.js';
import selectValue from '../../../../../../modules/Gull/Framework/selectValue.js';
import selectSearch from '../../../../../../modules/Gull/Framework/selectSearch.js';
import tooltips from '../../../../../../modules/Gull/Framework/tooltips.js';
import format from '../../../../../../modules/Gull/Framework/format.js';
import currency from 'currency.js';

/**
 * Module for handling multiple document reader view in insurance insight application.
 *
 * @type {Object}
 */
class VerzamelBeschikking {
	constructor() {
		/**
		 * Configuration config for the module.
		 * @type {Object}
		 * @property {number} initialDelay - Initial delay in milliseconds before first check.
		 * @property {number} requestInterval - Interval in milliseconds between subsequent checks.
		 * @property {string|null} step - Current step in the process.
		 * @property {string} url - URL for checking results.
		 */
		this.config = {
			initialDelay: 0,
			timeout: 60000,
			requestInterval: 1000,
			step: null,
			url: null,
			processStep: 2,
			path: '/check',
			rowSignalClasses: {
				success: 'table-success',
				warning: 'table-warning',
				danger: 'table-danger',
			},
			selectors: {
				form: '[data-docreader-form]',
				result: '[data-docreader-result-element]',
				progress: '[data-docreader-progress-element]',
				resultTable: '[data-docreader-result-table]',
				alertSuccess: '[data-docreader-alert-success-element]',
				alertWarning: '[data-docreader-alert-warning-element]',
				currentStep: '[data-docreader-current-step]',
				resultName: '[data-docreader-result-name]',
				submit: '[data-docreader-submit]',
				declaratiesAdd: '[data-docreader-declaraties-add]',
				declaratiesRemove: '[data-docreader-declaraties-remove]',
				declaratiesTemplate: '[data-docreader-declaraties-template]',
				factuurBedragTotal: '[data-docreader-factuur-bedrag-total]',
				factuurBedrag: '[data-docreader-factuur-bedrag]',
				selectDossier: '[data-docreader-select-dossier]',
				dossierNameOption: '[data-docreader-dossier-name-option]',
				dossierWerknemerIdOption: '[data-docreader-werknemer-id-option]',
			},
			el: null,
			levenshteinThreshold: 0.7,
		};
	}

	/**
	 * Initializes the module.
	 *
	 * @returns {void}
	 */
	init = () => {
		this.initConfig();
		this.initCheckResult();
	};

	/**
	 * Assigns DOM elements to an object based on provided selectors.
	 *
	 * @param {Object} selectors - An object containing selector strings as keys.
	 *
	 * @returns {Object} An object with the same keys as the selectors object, where each value is the corresponding DOM element or null if not found.
	 */
	initDomElements = (selectors) => {
		const elements = {};
		Object.keys(selectors).forEach((key) => {
			elements[key] = document.querySelector(selectors[key]) || null;
		});
		return elements;
	};

	/**
	 * Initializes the check result process.
	 * If the current step is '2' and a result element exists, it schedules a checkResult call after a specified delay.
	 *
	 * @returns {void}
	 */
	initCheckResult = () => {
		const step = this.config.step;
		const result = this.config.el.result;
		if (step !== null && step == '2' && result) {
			setTimeout(() => {
				this.checkResult();
				console.info('checkResult: Send initial request after ' + this.config.initialDelay + 'ms');
			}, this.config.initialDelay);
		}
	};

	/**
	 * Initializes the config for the view module.
	 *
	 * This function sets up the URL for AJAX requests and retrieves the current step from the DOM.
	 * It uses the formatAjaxUrl method to construct the URL and the getStepFromDOM method to get the current step.
	 *
	 * @returns {void}
	 */
	initConfig = () => {
		this.config.basePath = window.app.ajaxPath;
		this.config.el = this.initDomElements(this.config.selectors);
		this.config.url = this.formatAjaxUrl(this.config.basePath, this.config.processStep, this.config.path);
		this.config.step = this.getStepFromDOM(this.config.selectors.currentStep);
	};

	/**
	 * Initializes events for the module.
	 *
	 * @returns {void}
	 */
	initEvents = () => {
		// Monitor selects with [data-docreader-select-dossier] attribute using event delegation
		document.addEventListener('change', (event) => {
			if (event.target.matches(this.config.selectors.selectDossier)) {
				this.selectDossierEventHandler(event);
			}
		});

		// Monitor input fields with [data-factuur-bedrag] attribute using event delegation
		document.addEventListener('keyup', (event) => {
			if (event.target.matches(this.config.selectors.factuurBedrag)) {
				this.changeFactuurBedragEventHandler(event);
			}
		});

		// Monitor input fields with [data-factuur-bedrag] attribute using event delegation
		document.addEventListener('blur', (event) => {
			if (event.target.matches(this.config.selectors.factuurBedrag)) {
				this.changeFactuurBedragEventHandler(event);
			}
		});

		// Monitor button with [data-docreader-add-declaratie] attribute using event delegation
		document.addEventListener('click', (event) => {
			if (event.target.matches(this.config.selectors.declaratiesAdd)) {
				this.addDeclaratieRowEventHandler();
			}
		});

		// Monitor button with [data-docreader-remove-declaratie] attribute using event delegation
		document.addEventListener('click', (event) => {
			if (event.target.matches(this.config.selectors.declaratiesRemove)) {
				this.removeDeclaratieRowEventHandler(event);
			}
		});
	};

	/**
	 * Formats the AJAX URL by combining the base path, step, and path.
	 *
	 * @param {string} basePath - The base path of the URL.
	 * @param {string} step - The step to be included in the URL.
	 * @param {string} path - The specific path to be appended.
	 *
	 * @returns {string} The formatted AJAX URL.
	 */
	formatAjaxUrl = (basePath, step, path) => {
		const url = basePath + step + path;
		return url;
	};

	/**
	 * Handles the change of the factuur bedrag.
	 *
	 * @param {Event} event - The event object.
	 *
	 * @returns {void}
	 */
	changeFactuurBedragEventHandler = () => {
		const total = this.calculateTotal(this.config.el.resultTable, this.config.selectors.selectDossier, this.config.selectors.factuurBedrag);
		this.config.el.factuurBedragTotal.value = total;
	};

	/**
	 * Handles the change of the select dossier.
	 *
	 * @param {Event} event - The event object.
	 *
	 * @returns {void}
	 */
	selectDossierEventHandler = (event) => {
		const selectedValue = event.target.value;
		const rowElement = event.target.closest('tr');
		const total = this.calculateTotal(this.config.el.resultTable, this.config.selectors.selectDossier, this.config.selectors.factuurBedrag);

		this.setRowSignal(rowElement, selectedValue);
		this.config.el.factuurBedragTotal.value = total;
	};

	/**
	 * Sets the signal for a row based on the selected value.
	 *
	 * @param {Element} row - The row element.
	 * @param {string} selectedValue - The selected value.
	 *
	 * @returns {void}
	 */
	setRowSignal = (row, selectedValue) => {
		const classes = this.config.rowSignalClasses;
		row.classList.remove(classes.success, classes.warning, classes.danger);
		if (selectedValue === 'uitsluiten') {
			row.classList.add(classes.danger);
		} else if (selectedValue === '') {
			row.classList.add(classes.warning);
		} else {
			row.classList.add(classes.success);
		}
	};

	/**
	 * Calculates the total of valid rows.
	 *
	 * @todo: Test die dmv een mockup table met een tr kijkt of a.h.v. juiste string de juiste class wordt toegevoegd
	 *
	 * @param {Element} tableElement - The table element.
	 * @param {string} dossierSelector - The selector for the dossier.
	 * @param {string} factuurBedragSelector - The selector for the factuur bedrag.
	 *
	 * @returns {string} The total formatted as a currency string.
	 */
	calculateTotal = (tableElement, dossierSelector, factuurBedragSelector) => {
		let total = currency(0);
		const rows = tableElement.querySelectorAll('tbody tr');
		rows.forEach((row) => {
			const select = row.querySelector(dossierSelector);
			const factuurBedrag = row.querySelector(factuurBedragSelector);
			const selectedValue = select.value;
			if (selectedValue !== 'uitsluiten' && selectedValue !== '') {
				const amount = currency(factuurBedrag.value, { separator: '', decimal: ',' });
				total = total.add(amount);
			}
		});
		return total.format({ separator: '', decimal: ',', symbol: '' });
	};

	/**
	 * Retrieves the current step from the DOM.
	 *
	 * @todo: test die dmv een mockup de step uit de dom haalt
	 *
	 * @param {string} selector - The selector for the current step.
	 *
	 * @returns {void}
	 */
	getStepFromDOM = (selector) => {
		const element = document.querySelector(selector);
		const step = element.dataset.docreaderCurrentStep;
		const stepInt = parseInt(step);
		return stepInt;
	};

	/**
	 * Checks the result by making an AJAX request.
	 *
	 * @returns {void}
	 */
	checkResult = () => {
		const startTime = Date.now();
		const checkTimeout = () => {
			if (Date.now() - startTime > this.config.timeout) {
				this.domShowResults('', true);
				console.warn('Error: Request timed out');
				return;
			}
			fetch(this.config.url)
				.then((response) => response.text())
				.then((data) => {
					const parsedData = JSON.parse(data);
					console.info(parsedData);
					try {
						const parsedData = JSON.parse(data);
						if (parsedData.status == 'inProgress') {
							console.info('Send request: No result yet...');
							setTimeout(checkTimeout, this.config.requestInterval);
						} else if (parsedData.status == 'success') {
							this.domShowResults(parsedData.template);
						} else if (parsedData.status == 'failed') {
							this.domShowResults(parsedData.template, true);
							console.warn('failed');
						}
					} catch (error) {
						console.warn('Error parsing JSON:', error);
					}
				})
				.catch((error) => {
					console.warn('Error:', error);
				});
		};
		checkTimeout();
	};

	/**
	 * Applies the result to the DOM.
	 *
	 * @param {string} data - The result data to be applied.
	 * @param {boolean} error - Whether the result is an error.
	 *
	 * @returns {void}
	 */
	domShowResults = (data, error = false) => {
		this.config.el.result.innerHTML = data;
		this.config.el.result.classList.remove('d-none');
		this.config.el.progress.classList.add('d-none');

		this.config.el = this.initDomElements(this.config.selectors);

		if (error) {
			this.config.el.alertWarning.classList.remove('d-none');
			this.addDeclaratieRow(this.config.el.resultTable?.querySelector('tbody'), this.config.el.declaratiesTemplate);
		} else {
			this.config.el.alertSuccess.classList.remove('d-none');
		}

		tooltips.init(this.config.el.form);
		selectValue.init(this.config.el.form);
		selectToggle.init(this.config.el.form);
		datepicker.init(this.config.el.form);
		format.init(this.config.el.form);
		selectSearch.init(this.config.el.form);

		this.initEvents();

		if (!error) {
			this.matchNamesInTable(this.config.el.resultTable, this.config.selectors.resultName, this.config.selectors.selectDossier, this.config.selectors.dossierNameOption);
		}
	};

	/**
	 * Calculates the Levenshtein distance between two strings.
	 *
	 * @todo: test genereren
	 *
	 * @param {string} a - The first string.
	 * @param {string} b - The second string.
	 *
	 * @returns {number} The Levenshtein distance between the two strings.
	 */
	levenshteinDistance = (a, b) => {
		const matrix = [];

		// Increment along the first column of each row
		for (let i = 0; i <= b.length; i++) {
			matrix[i] = [i];
		}

		// Increment each column in the first row
		for (let j = 0; j <= a.length; j++) {
			matrix[0][j] = j;
		}

		// Fill the rest of the matrix
		for (let i = 1; i <= b.length; i++) {
			for (let j = 1; j <= a.length; j++) {
				if (b.charAt(i - 1) === a.charAt(j - 1)) {
					matrix[i][j] = matrix[i - 1][j - 1];
				} else {
					matrix[i][j] = Math.min(
						matrix[i - 1][j - 1] + 1, // substitution
						matrix[i][j - 1] + 1, // insertion
						matrix[i - 1][j] + 1 // deletion
					);
				}
			}
		}

		return matrix[b.length][a.length];
	};

	/**
	 * Finds the closest match based on Levenshtein Distance and additional criteria.
	 *
	 * @todo: test genereren die string matcht met een array van objecten
	 *
	 * @param {string} targetName - The target name.
	 * @param {Array} optionsArray - The array of options.
	 *
	 * @returns {Element|null} The closest matching option or null if no match is found.
	 */
	findClosestMatch = (targetName, optionsArray, threshold) => {
		let closestMatch = null;
		let minDistance = Infinity;
		optionsArray.forEach((item) => {
			const distance = this.levenshteinDistance(targetName.toLowerCase(), item.werknemerMatchNaam.toLowerCase());
			const maxLength = Math.max(targetName.length, item.werknemerMatchNaam.length);
			const normalizedDistance = distance / maxLength;
			if (normalizedDistance < threshold && normalizedDistance < minDistance) {
				minDistance = normalizedDistance;
				closestMatch = item;
			}
		});

		return closestMatch;
	};

	/**
	 * Matches the names in the table.
	 *
	 * @todo: tableElement en inputFieldSelector en dossierNameSelector en dossierNameOptionSelector meegeven en testen met mockup
	 *
	 * @param {Element} tableElement - The table element.
	 * @param {string} inputFieldSelector - The selector for the input field.
	 * @param {string} dossierNameSelector - The selector for the dossier name.
	 * @param {string} dossierNameOptionSelector - The selector for the dossier name option.
	 *
	 * @returns {void}
	 */
	matchNamesInTable = (tableElement, inputFieldSelector, dossierNameSelector, dossierNameOptionSelector) => {
		const rows = tableElement.querySelectorAll('tbody tr');

		rows.forEach((row) => {
			const inputField = row.querySelector(inputFieldSelector);
			const targetName = inputField.value.trim();
			const select = row.querySelector(dossierNameSelector);
			const optionsElements = select.querySelectorAll(dossierNameOptionSelector);
			const optionsArray = this.mapOptionsToArray(optionsElements);

			const resultDatumEersteAoDag = inputField.dataset.docreaderResultWerknemerInitieleBegindatum;

			const filteredOptionsArray = optionsArray.filter((option) => option.werknemerInitieleBegindatum == resultDatumEersteAoDag);

			let closestMatchedOption = null;
			if (filteredOptionsArray.length > 0) {
				closestMatchedOption = this.findClosestMatch(targetName, filteredOptionsArray, this.config.levenshteinThreshold);
			}

			if (closestMatchedOption) {
				select.value = closestMatchedOption.werknemerMeldingId || '';
				$(select).trigger('change');
				this.setRowSignal(row, closestMatchedOption);
			}

			// this.logMatchData(this.config.levenshteinThreshold, targetName, resultDatumEersteAoDag, filteredOptionsArray, closestMatchedOption ?? null);

		});
	};

	logMatchData = (levenshteinThreshold, targetName, initieleBegindatum, filteredOptionsArray, closestMatchedOption) => {
		console.info(`Matching data for: ${targetName}`);
		console.info(`InitieleBegindatum: ${initieleBegindatum}`);
		console.info('Matched resultDatumEersteAoDag:', filteredOptionsArray);
		console.info(`Levenshtein threshold: ${levenshteinThreshold}`);
		console.info('Closest matched option:', closestMatchedOption);
		console.info('--------------------------------');
	};

	/**
	 * Checks if the werknemerId appears more than once in the options array.
	 *
	 * @param {Array} optionsArray - The array of options.
	 * @param {string} werknemerId - The werknemerId to check.
	 *
	 * @returns {boolean} True if the werknemerId appears more than once, false otherwise.
	 */
	hasDuplicateWerknemerId = (optionsArray, werknemerId) => {
		const count = optionsArray.filter((option) => option.werknemerId === werknemerId).length;
		return count > 1;
	};

	/**
	 * Maps option elements to an array of objects containing werknemerId and werknemerNaam.
	 *
	 * @param {NodeList} options - The NodeList of option elements.
	 *
	 * @returns {Array} An array of objects with werknemerId and werknemerNaam properties.
	 */
	mapOptionsToArray = (options) => {
		return Array.from(options).map((option) => ({
			werknemerMeldingId: option.value,
			werknemerNaam: option.dataset.docreaderDossierNameOption,
			werknemerMatchNaam: option.dataset.docreaderDossierMatchNameOption,
			werknemerId: option.dataset.docreaderWerknemerIdOption,
			werknemerInitieleBegindatum: option.dataset.docreaderWerknemerInitieleBegindatumOption,
		}));
	};

	/**
	 * Handles the addition of a declaratie row.
	 *
	 * @returns {void}
	 */
	addDeclaratieRowEventHandler = () => {
		const tableBodyElement = this.config.el.resultTable.querySelector('tbody');
		const templateElement = this.config.el.declaratiesTemplate;
		const clone = this.addDeclaratieRow(tableBodyElement, templateElement);

		this.renumberDeclaraties(tableBodyElement, clone, 'declaraties');

		datepicker.init(clone);
		format.init(clone);
		tooltips.init(clone);
	};

	/**
	 * Adds a declaratie row to the table.
	 *
	 * @todo: test die op basis van een mockup kijkt of er een tr wordt toegevoegd aan table op bassis van template
	 *
	 * @param {Element} templateElement - The template element.
	 *
	 * @returns {Element|null} The cloned element or null if the template element is not found.§
	 */
	addDeclaratieRow = (tableBodyElement, templateElement) => {
		const clone = templateElement?.content.cloneNode(true) || null;
		if (clone) {
			tableBodyElement.appendChild(clone);
		}
		return clone;
	};

	/**
	 * Renumbers the declaratie rows.
	 *
	 * @todo: test die op basis van een mockup kijkt of input en select elementen in een table worden genummerd
	 *
	 * @param {Element} tableBodyElement - The table body element.
	 * @param {Element} clone - The clone element.
	 * @param {string} baseName - The base name.
	 *
	 * @returns {void}
	 */
	renumberDeclaraties = (tableBodyElement, clone, baseName) => {
		tableBodyElement.querySelectorAll('tr').forEach((row, index) => {
			row.querySelectorAll('input, select').forEach((element) => {
				const dataNameValue = element.getAttribute('data-docreader-name');
				if (dataNameValue) {
					const name = `${baseName}[${index + 1}][${dataNameValue}]`;
					element.setAttribute('name', name);
				}
			});
		});
	};

	/**
	 * Handles the removal of a declaratie row.
	 *
	 * @param {Event} event - The event object triggered by clicking the remove button.
	 *
	 * @returns {void}
	 */
	removeDeclaratieRowEventHandler = (event) => {
		this.removeDeclaratieRow(event.target);
	};

	/**
	 * Removes a declaratie row from the table.
	 *
	 * @param {Element} target - The element that triggered the removal.
	 *
	 * @todo: test die op basis van een mockup kijkt of regel wordt verwijderd waar het target element zich in bevindt
	 *
	 * @returns {void}
	 */
	removeDeclaratieRow = (target) => {
		const row = target.closest('tr');
		if (row) {
			row.remove();
		}
	};
}

export default VerzamelBeschikking;
